import {
  MemberSummaryFeedbackCreateRequest,
  MemberSummaryFeedbackForm,
  MemberSummaryFeedbackFormSchema,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { MemberSummaryDto } from '@act/shared/data-transfer-objects';
import { Dialog, DialogActions, DialogContent, Divider } from '@mui/material';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { memberSummaryRequests } from 'redux/requests/memberSummaryRequests';
import {
  getFeedbackDescriptions,
} from 'redux/slices/metaSlice.slice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import {
  DialogContentContainer,
  DialogTitleBarComponent,
} from './components/SharedDialogHookComponents';
import { Button, FormBuilder } from '@act/storybook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { Typography } from '@mui/material';
import { useAppDispatch } from 'redux/storeHooks';
import { zodResolver } from '@hookform/resolvers/zod';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import { useToast } from '../app/ReusableToast';
import { TrackingProvider } from 'react-components/tracking';

export interface MemberSummaryFeedbackDialogUniqueProps {
  member: UserDto;
  memberSummary: MemberSummaryDto;
  handleCallback: (p: boolean) => void;
}

export type MemberSummaryFeedbackDialogProps = GenericDialogProps<boolean> &
  MemberSummaryFeedbackDialogUniqueProps;

export type MemberSummaryFeedbackDialogCallbackInput = {};

export const MemberSummaryFeedbackDialog = ({
  memberSummary,
  open,
  setOpen,
  callback,
  handleCallback,
}: MemberSummaryFeedbackDialogProps) => {
  const [
    newMemberSummaryRequestPayload,
    isCreatingNewMemberSummaryFeedback,
    createMemberSummaryFeedback,
  ] = useApiCall(memberSummaryRequests.createNewMemberSummaryFeedback);
  const feedbackDescriptions =
    metaSliceSelectors.useSelectFeedbackDescriptions();
  const dispatch = useAppDispatch();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const toast = useToast();
  useEffect(() => {
    if (!newMemberSummaryRequestPayload) return;
    const selectedFeedbackDescription = feedbackDescriptions.collection.find(
      fb => fb.id === newMemberSummaryRequestPayload.feedbackDescriptionId
    );
    handleCallback(!selectedFeedbackDescription.isPositive);
    toast('Feedback submitted successfully');
    setOpen(false);
  }, [newMemberSummaryRequestPayload]);

  useEffect(() => {
    if (feedbackDescriptions.status === 'initial') {
      dispatch(getFeedbackDescriptions({}));
    }
  }, []);
  const { control, handleSubmit, formState } =
    useForm<MemberSummaryFeedbackForm>({
      defaultValues: {
        feedbackDescriptionId: null,
        comment: '',
      },
      resolver: zodResolver(MemberSummaryFeedbackFormSchema),
    });

  async function onSubmit(data: MemberSummaryFeedbackForm) {
    const submitData = {
      ...data,
      summary: memberSummary.summary,
      summaryDataId: memberSummary.summaryDataId,
      memberId: memberSummary.userId,
    };
    createMemberSummaryFeedback({
      body: submitData as MemberSummaryFeedbackCreateRequest,
      params: null,
    });
  }
  const { isValid, isDirty } = formState;

  const formConfig: FormConfig<MemberSummaryFeedbackForm> = [
    {
      type: 'input',
      name: 'feedbackDescriptionId',
      label: 'Feedback',
      input: 'radioGroup',
      props: {
        options: feedbackDescriptions.collection.map(description => {
          return {
            key: description.id,
            label: description.description,
            value: description.id,
          };
        }),
        trackingName: 'feedback-description-radio-group',
      },
    },
    {
      type: 'passthrough',
      content: (
        <Typography variant="titleMedium" sx={{ padding: '8px 0' }}>
          Add details to help Reema improve future summaries
        </Typography>
      ),
    },

    {
      type: 'input',
      name: 'comment',
      label: 'Feedback',
      input: 'text',
      props: {
        multiline: true,
        trackingName: 'feedback-comment-input',
      },
    },
  ];

  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
        callback(true);
      });
    } else {
      setOpen(false);
      callback(true);
    }
  };

  return (
    <TrackingProvider contextName="member-summary-feedback-dialog">
      <div style={{ width: '600px' }}>
        <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogContentContainer>
          <DialogTitleBarComponent
            title="Send feedback"
            handleClose={handleClose}
          />
          <DialogContent>
            <Typography variant="titleMedium" sx={{ padding: '8px 0' }}>
              Which description best fits the member summary?
            </Typography>

            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
            />
          </DialogContent>
          <Divider />
          <DialogActions sx={{ padding: '8px 24px' }}>
            <Button
              onClick={handleClose}
              trackingName="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!isValid || isCreatingNewMemberSummaryFeedback}
              type="submit"
              debounced
              onClick={handleSubmit(onSubmit)}
              sx={{ padding: '10px 24px' }}
              trackingName="submit-feedback-button"
            >
              Submit
            </Button>
          </DialogActions>
        </DialogContentContainer>
      </Dialog>
    </div>
    </TrackingProvider>
  );
};
